import { Reducer } from 'redux';
import { createAction, ActionTypes } from 'client/core/store/actions';
import { useSelector, shallowEqual } from 'react-redux';
import { RootState } from 'client/core/store/RootReducer';
import { PermissionsType } from 'common/permissions/Permissions';
import { PermissionLogic } from 'common/permissions/PermissionLogic';
import { AuthStorage } from './AuthStorage';
import { UserDto } from 'common/dto/UserDto';
import { isRoleAdministrator } from './isRoleAdministrator';

export const AuthActions = {
  loginSucceed: createAction(
    '@auth/loginSucceed',
    (strategy: string, nonce: string) => ({
      strategy,
      nonce
    })
  ),
  setToken: createAction('@auth/setToken', (token: string) => ({ token })),
  tokenRefreshed: createAction(
    '@auth/tokenRefreshed',
    (strategy: string, nonce: string) => ({
      strategy,
      nonce
    })
  ),
  logout: createAction('@auth/logout', (forceLogout: boolean) => ({
    forceLogout
  })),
  tokenExpired: createAction('@auth/tokenExpired'),
  currentLoaded: createAction('@auth/currentLoaded', (current: UserDto) => ({
    current
  }))
};

export interface AuthState {
  isLogged: boolean;
  isLogoutForced: boolean;
  strategy: string | null;
  token: string | null;
  /**
   * Viene fornito un nonce randomico ogni volta che viene effettuato un nuovo
   * login (chiamata ad access token).
   */
  nonce: string;
  isTokenExpired: boolean;
  current: UserDto | null;
}

export const AuthReducer: Reducer<
  AuthState,
  ActionTypes<typeof AuthActions>
> = (
  state = {
    isLogoutForced: false,
    nonce: 'first-load',
    isTokenExpired: false,
    current: null,
    /**
     * Carichiamo i dati iniziali dal `localStorage` in caso siano
     * presenti.
     */
    ...AuthStorage.initialState()
  },
  action
) => {
  switch (action.type) {
    /**
     * Al login, salviamo il token.
     */
    case '@auth/loginSucceed': {
      const { strategy, nonce } = action.payload;
      AuthStorage.saveState({ strategy, isLogged: true, token: null });
      return {
        ...state,
        isLogged: true,
        strategy,
        nonce,
        isTokenExpired: false,
        current: null
      };
    }

    case '@auth/setToken': {
      const { token } = action.payload;
      AuthStorage.saveState({
        strategy: state.strategy,
        isLogged: true,
        token
      });
      return {
        ...state,
        token
      };
    }

    /**
     * Quando il token viene refreshato correttamente, non reimposto il
     * `current` perché è ancora valido (almeno parzialmente) il precedente.
     */
    case '@auth/tokenRefreshed': {
      const { strategy, nonce } = action.payload;
      AuthStorage.saveState({
        strategy: state.strategy,
        isLogged: true,
        token: state.token
      });
      return {
        ...state,
        isLogged: true,
        strategy,
        nonce,
        isTokenExpired: false
      };
    }

    /**
     * Utente corrente caricato. Quando questa richiesta ha esito positivo,
     * vuol dire che l'utente è autorizzato quindi possiamo impostare il token.
     */
    case '@auth/currentLoaded':
      AuthStorage.saveState({
        strategy: state.strategy,
        isLogged: true,
        token: state.token
      });
      return {
        ...state,
        isLogged: true,
        isTokenExpired: false,
        current: action.payload.current
      };

    /**
     * In caso di scadenza del token lo registriamo.
     */
    case '@auth/tokenExpired':
      return { ...state, isTokenExpired: true };

    /**
     * Rimozione del token dalla sessione.
     */
    case '@auth/logout':
      AuthStorage.saveState({ strategy: null, isLogged: false, token: null });
      return {
        ...state,
        isLogged: false,
        isLogoutForced: action.payload.forceLogout,
        token: null,
        strategy: null,
        nonce: 'logout',
        isTokenExpired: false,
        current: null
      };

    default:
      return state;
  }
};

/**
 * Hook per sapere se si è loggati.
 */
export const useAuth = () =>
  useSelector(
    (state: RootState) => ({
      logged: state.auth.isLogged,
      isLogoutForced: state.auth.isLogoutForced,
      strategy: state.auth.strategy,
      nonce: state.auth.nonce,
      accessToken: state.auth.token
    }),
    shallowEqual
  );

export const useCurrentUser = () =>
  useSelector((state: RootState) => state.auth.current, shallowEqual);

/**
 * Hook per controllare i permessi
 */
export const usePermissions = () => {
  const user = useCurrentUser();
  return {
    has(permission: PermissionsType | PermissionsType[]) {
      return PermissionLogic.has(user, permission);
    }
  };
};

/**
 * Hook per controllare se l'utente loggato è amministratore.
 */
export const useIsCurrentUserAdministrator = () => {
  const currentUser = useCurrentUser();
  return isRoleAdministrator(currentUser?.roleId);
};
